import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart3, TrendingUp, Users, Truck, Egg, Boxes, CalendarDays, Layers, Trophy,
  Download, FileImage, FileText, ArrowLeft, ChevronRight
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList
} from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

/* ------------------------------------------------------------------ */
/* THEME — change these tokens to restyle the whole analytics page.   */
/* ------------------------------------------------------------------ */
const THEME = {
  bg: '#0c0d1a',
  panel: '#15172a',
  panelSoft: '#1c1f38',
  border: '#2b2e4d',
  text: '#eceef7',
  textMuted: '#9aa0c8',
  accent: '#8b5cf6',
  accent2: '#f59e0b',
  grid: '#262a47',
  series: ['#8b5cf6', '#22d3ee', '#f59e0b', '#34d399', '#f43f5e', '#e879f9', '#38bdf8', '#fb7185']
};

const fmt = (n) => new Intl.NumberFormat('en-NG').format(Math.round(n || 0));
const fmtNaira = (n) => '₦' + new Intl.NumberFormat('en-NG', { maximumFractionDigits: 0 }).format(Math.round(n || 0));
const compact = (n) => {
  const v = Math.round(n || 0);
  if (Math.abs(v) >= 1000000) return (v / 1000000).toFixed(1) + 'M';
  if (Math.abs(v) >= 1000) return (v / 1000).toFixed(1) + 'k';
  return String(v);
};

const Panel = ({ title, icon: Icon, children, subtitle, testid, action }) => (
  <div
    data-testid={testid}
    style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 18 }}
    className="p-5 shadow-xl"
  >
    <div className="flex items-center justify-between gap-2 mb-4">
      <div className="flex items-center gap-2">
        {Icon && (
          <span style={{ background: THEME.panelSoft, color: THEME.accent }} className="w-9 h-9 rounded-xl flex items-center justify-center">
            <Icon className="w-5 h-5" />
          </span>
        )}
        <div>
          <h3 style={{ color: THEME.text }} className="font-semibold leading-tight">{title}</h3>
          {subtitle && <p style={{ color: THEME.textMuted }} className="text-xs">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
    {children}
  </div>
);

const PickupAnalytics = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [view, setView] = useState('monthly'); // 'monthly' | 'yearly'
  const [metric, setMetric] = useState('crates'); // 'crates' | 'eggs' | 'revenue'
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [exporting, setExporting] = useState(false);
  const captureRef = useRef(null);

  const years = [];
  for (let y = currentYear - 2; y <= currentYear + 1; y++) years.push(y);

  const isRevenue = metric === 'revenue';
  const unit = metric === 'crates' ? 'crates' : metric === 'eggs' ? 'eggs' : '₦';
  const showVal = (v) => (isRevenue ? fmtNaira(v) : fmt(v));
  const axisFmt = (v) => (isRevenue ? '₦' + compact(v) : compact(v));
  const labelFmt = (v) => (!v ? '' : (isRevenue ? '₦' + compact(v) : compact(v)));

  useEffect(() => { fetchData(); setSelectedDistributor(null); /* eslint-disable-next-line */ }, [year]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/order-pickup-analytics?year=${year}`, getAuthHeader());
      setData(res.data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type) => {
    if (!captureRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(captureRef.current, {
        backgroundColor: THEME.bg,
        scale: 2,
        useCORS: true,
        logging: false
      });
      const fileBase = `pickup-analytics-${year}-${metric}`;
      if (type === 'png') {
        const link = document.createElement('a');
        link.download = `${fileBase}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        toast.success('PNG downloaded');
      } else {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageW = pdf.internal.pageSize.getWidth();
        const imgW = pageW - 16;
        const imgH = (canvas.height * imgW) / canvas.width;
        pdf.setFillColor(12, 13, 26);
        pdf.rect(0, 0, pageW, pdf.internal.pageSize.getHeight(), 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(14);
        pdf.text(`Pick-up Analytics — ${year}`, 8, 10);
        let position = 14;
        let heightLeft = imgH;
        const pageH = pdf.internal.pageSize.getHeight() - position;
        pdf.addImage(imgData, 'PNG', 8, position, imgW, imgH);
        heightLeft -= pageH;
        while (heightLeft > 0) {
          pdf.addPage();
          pdf.setFillColor(12, 13, 26);
          pdf.rect(0, 0, pageW, pdf.internal.pageSize.getHeight(), 'F');
          position = heightLeft - imgH + 8;
          pdf.addImage(imgData, 'PNG', 8, position, imgW, imgH);
          heightLeft -= pdf.internal.pageSize.getHeight();
        }
        pdf.save(`${fileBase}.pdf`);
        toast.success('PDF downloaded');
      }
    } catch (e) {
      console.error(e);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const trendData = view === 'monthly'
    ? (data?.monthly || []).map(m => ({ name: m.label, value: m[metric] }))
    : (data?.yearly || []).map(y => ({ name: String(y.year), value: y[metric] }));

  const topCustomers = (data?.top_customers || []).map(c => ({ name: c.name, value: c[metric] }));
  const distributors = (data?.distributors || []).filter(d => d[metric] > 0).map(d => ({ name: d.name, value: d[metric] }));
  const eggSizes = (data?.egg_sizes || []).map(s => ({ name: s.label, value: s[metric] }));
  const distCustomers = data?.distributor_customers || {};
  const drillData = selectedDistributor
    ? (distCustomers[selectedDistributor] || []).filter(c => c[metric] > 0).map(c => ({ name: c.name, value: c[metric] }))
    : [];

  const t = data?.totals || {};
  const totalMetricVal = isRevenue ? t.revenue : (metric === 'crates' ? t.crates : t.eggs);
  const totalLabel = isRevenue ? 'Total Revenue' : (metric === 'crates' ? 'Total Crates' : 'Total Eggs');

  const stats = [
    { label: totalLabel, value: showVal(totalMetricVal), icon: isRevenue ? BarChart3 : Boxes, color: THEME.series[0] },
    { label: 'Active Customers', value: fmt(t.customers), icon: Users, color: THEME.series[1] },
    { label: 'Distributors', value: fmt(t.distributors), icon: Truck, color: THEME.series[2] },
    { label: 'Best Month', value: t.best_month || '—', icon: Trophy, color: THEME.series[3] },
  ];

  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div style={{ background: THEME.panelSoft, border: `1px solid ${THEME.border}`, borderRadius: 12 }} className="px-3 py-2 shadow-lg">
        {label != null && <div style={{ color: THEME.text }} className="text-xs font-semibold mb-1">{label}</div>}
        {payload.map((p, i) => (
          <div key={i} className="text-xs flex items-center gap-2" style={{ color: THEME.textMuted }}>
            <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill || p.payload?.fill }} />
            <span style={{ color: THEME.text }}>{showVal(p.value)}</span> {!isRevenue && unit}
          </div>
        ))}
      </div>
    );
  };

  const SegBtn = ({ active, onClick, children, testid }) => (
    <button
      onClick={onClick}
      data-testid={testid}
      style={{ background: active ? THEME.accent : 'transparent', color: active ? '#fff' : THEME.textMuted }}
      className="px-4 py-1.5 rounded-lg text-sm font-medium transition-colors"
    >
      {children}
    </button>
  );

  return (
    <div style={{ background: THEME.bg, borderRadius: 24 }} className="p-4 sm:p-6 -m-2 sm:-m-4" data-testid="pickup-analytics-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <span style={{ background: THEME.accent }} className="w-11 h-11 rounded-2xl flex items-center justify-center shadow-lg">
            <BarChart3 className="w-6 h-6 text-white" />
          </span>
          <div>
            <h1 style={{ color: THEME.text }} className="text-2xl sm:text-3xl font-bold tracking-tight">Pick-up Analytics</h1>
            <p style={{ color: THEME.textMuted }} className="text-sm">Visual insights across customers, distributors & the whole farm</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Metric toggle */}
          <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}` }} className="flex items-center gap-1 p-1 rounded-xl">
            <SegBtn active={metric === 'crates'} onClick={() => setMetric('crates')} testid="metric-crates-btn">Crates</SegBtn>
            <SegBtn active={metric === 'eggs'} onClick={() => setMetric('eggs')} testid="metric-eggs-btn">Eggs</SegBtn>
            <SegBtn active={metric === 'revenue'} onClick={() => setMetric('revenue')} testid="metric-revenue-btn">₦ Revenue</SegBtn>
          </div>
          {/* Year select */}
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            data-testid="analytics-year-select"
            style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, color: THEME.text }}
            className="px-3 py-2 rounded-xl text-sm font-medium outline-none cursor-pointer"
          >
            {years.map(y => <option key={y} value={y} style={{ color: '#000' }}>{y}</option>)}
          </select>
          {/* Export */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="export-analytics-btn"
                disabled={exporting || loading}
                style={{ background: THEME.accent, color: '#fff', opacity: (exporting || loading) ? 0.6 : 1 }}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold shadow-lg"
              >
                {exporting
                  ? <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  : <Download className="w-4 h-4" />}
                Export
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('png')} className="gap-2 cursor-pointer" data-testid="export-png-item">
                <FileImage className="w-4 h-4 text-emerald-600" /> Download as PNG
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')} className="gap-2 cursor-pointer" data-testid="export-pdf-item">
                <FileText className="w-4 h-4 text-red-600" /> Download as PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin w-9 h-9 border-4 rounded-full" style={{ borderColor: THEME.accent, borderTopColor: 'transparent' }} />
          <span style={{ color: THEME.textMuted }} className="ml-3">Crunching the numbers…</span>
        </div>
      ) : (
        <div ref={captureRef} style={{ background: THEME.bg }} className="space-y-5 rounded-2xl">
          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-stat-cards">
            {stats.map((s, i) => (
              <div key={i} style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 18 }} className="p-4 shadow-lg">
                <div className="flex items-center justify-between">
                  <span style={{ color: THEME.textMuted }} className="text-xs font-medium">{s.label}</span>
                  <span style={{ background: THEME.panelSoft, color: s.color }} className="w-8 h-8 rounded-lg flex items-center justify-center">
                    <s.icon className="w-4 h-4" />
                  </span>
                </div>
                <div style={{ color: THEME.text }} className="text-2xl font-bold mt-2 truncate">{s.value}</div>
              </div>
            ))}
          </div>

          {/* Trend chart with view switch */}
          <Panel
            testid="trend-panel"
            title={view === 'monthly' ? `Monthly Trend — ${year}` : 'Yearly Trend'}
            subtitle={`Pick-ups measured in ${isRevenue ? 'naira revenue' : unit}`}
            icon={view === 'monthly' ? CalendarDays : TrendingUp}
            action={
              <div style={{ background: THEME.panelSoft, border: `1px solid ${THEME.border}` }} className="flex items-center gap-1 p-1 rounded-xl">
                <SegBtn active={view === 'monthly'} onClick={() => setView('monthly')} testid="view-monthly-btn">Monthly</SegBtn>
                <SegBtn active={view === 'yearly'} onClick={() => setView('yearly')} testid="view-yearly-btn">Yearly</SegBtn>
              </div>
            }
          >
            <ResponsiveContainer width="100%" height={320}>
              {view === 'monthly' ? (
                <BarChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={THEME.grid} vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: THEME.textMuted, fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={axisFmt} tick={{ fill: THEME.textMuted, fontSize: 12 }} axisLine={false} tickLine={false} width={55} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: THEME.panelSoft }} />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]} barSize={28}>
                    {trendData.map((_, i) => <Cell key={i} fill={THEME.accent} />)}
                    <LabelList dataKey="value" position="top" formatter={labelFmt} style={{ fill: THEME.text, fontSize: 11, fontWeight: 600 }} />
                  </Bar>
                </BarChart>
              ) : (
                <BarChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={THEME.grid} vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: THEME.textMuted, fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={axisFmt} tick={{ fill: THEME.textMuted, fontSize: 12 }} axisLine={false} tickLine={false} width={55} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: THEME.panelSoft }} />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]} barSize={46}>
                    {trendData.map((_, i) => <Cell key={i} fill={THEME.accent2} />)}
                    <LabelList dataKey="value" position="top" formatter={labelFmt} style={{ fill: THEME.text, fontSize: 11, fontWeight: 600 }} />
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          </Panel>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Top customers */}
            <Panel testid="top-customers-panel" title="Top Customers" subtitle={`Ranked by ${isRevenue ? 'revenue' : unit} — ${year}`} icon={Users}>
              {topCustomers.length === 0 ? (
                <EmptyChart />
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(240, topCustomers.length * 38)}>
                  <BarChart data={topCustomers} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={THEME.grid} horizontal={false} />
                    <XAxis type="number" tickFormatter={axisFmt} tick={{ fill: THEME.textMuted, fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="name" tick={{ fill: THEME.textMuted, fontSize: 12 }} axisLine={false} tickLine={false} width={110} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: THEME.panelSoft }} />
                    <Bar dataKey="value" radius={[0, 8, 8, 0]} barSize={20}>
                      {topCustomers.map((_, i) => <Cell key={i} fill={THEME.series[i % THEME.series.length]} />)}
                      <LabelList dataKey="value" position="right" formatter={labelFmt} style={{ fill: THEME.text, fontSize: 11, fontWeight: 600 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Panel>

            {/* By distributor + drill-down */}
            <Panel
              testid="distributor-panel"
              title={selectedDistributor ? selectedDistributor : 'Share by Distributor'}
              subtitle={selectedDistributor ? `Customer breakdown — ${year}` : `Tap a slice to drill down — ${year}`}
              icon={Truck}
              action={selectedDistributor ? (
                <button
                  onClick={() => setSelectedDistributor(null)}
                  data-testid="distributor-back-btn"
                  style={{ background: THEME.panelSoft, color: THEME.text, border: `1px solid ${THEME.border}` }}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> All distributors
                </button>
              ) : null}
            >
              {!selectedDistributor ? (
                distributors.length === 0 ? <EmptyChart /> : (
                  <>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={distributors} dataKey="value" nameKey="name"
                          cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3}
                          onClick={(slice) => slice?.name && setSelectedDistributor(slice.name)}
                          style={{ cursor: 'pointer', outline: 'none' }}
                        >
                          {distributors.map((_, i) => <Cell key={i} fill={THEME.series[i % THEME.series.length]} stroke={THEME.panel} strokeWidth={2} style={{ cursor: 'pointer', outline: 'none' }} />)}
                        </Pie>
                        <Tooltip content={<ChartTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span style={{ color: THEME.textMuted }}>{v}</span>} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-2 space-y-1" data-testid="distributor-list">
                      {distributors.map((d, i) => (
                        <button
                          key={i}
                          onClick={() => setSelectedDistributor(d.name)}
                          style={{ background: THEME.panelSoft, border: `1px solid ${THEME.border}` }}
                          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm hover:opacity-90 transition-opacity"
                          data-testid={`distributor-row-${i}`}
                        >
                          <span className="flex items-center gap-2" style={{ color: THEME.text }}>
                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: THEME.series[i % THEME.series.length] }} />
                            {d.name}
                          </span>
                          <span className="flex items-center gap-2" style={{ color: THEME.textMuted }}>
                            {showVal(d.value)} <ChevronRight className="w-4 h-4" />
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )
              ) : (
                drillData.length === 0 ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={Math.max(240, drillData.length * 40)}>
                    <BarChart data={drillData} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={THEME.grid} horizontal={false} />
                      <XAxis type="number" tickFormatter={axisFmt} tick={{ fill: THEME.textMuted, fontSize: 12 }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" tick={{ fill: THEME.textMuted, fontSize: 12 }} axisLine={false} tickLine={false} width={110} />
                      <Tooltip content={<ChartTooltip />} cursor={{ fill: THEME.panelSoft }} />
                      <Bar dataKey="value" radius={[0, 8, 8, 0]} barSize={22}>
                        {drillData.map((_, i) => <Cell key={i} fill={THEME.series[i % THEME.series.length]} />)}
                        <LabelList dataKey="value" position="right" formatter={labelFmt} style={{ fill: THEME.text, fontSize: 11, fontWeight: 600 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )
              )}
            </Panel>
          </div>

          {/* Egg-size breakdown */}
          <Panel testid="egg-size-panel" title="Egg-Size Breakdown" subtitle={`Volume by size in ${isRevenue ? 'revenue' : unit} — ${year}`} icon={Layers}>
            {eggSizes.every(s => s.value === 0) ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={eggSizes} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={THEME.grid} vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: THEME.textMuted, fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={axisFmt} tick={{ fill: THEME.textMuted, fontSize: 12 }} axisLine={false} tickLine={false} width={55} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: THEME.panelSoft }} />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]} barSize={46}>
                    {eggSizes.map((_, i) => <Cell key={i} fill={THEME.series[i % THEME.series.length]} />)}
                    <LabelList dataKey="value" position="top" formatter={labelFmt} style={{ fill: THEME.text, fontSize: 11, fontWeight: 600 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </div>
      )}
    </div>
  );
};

const EmptyChart = () => (
  <div className="flex flex-col items-center justify-center py-16">
    <Egg className="w-10 h-10 mb-3" style={{ color: THEME.border }} />
    <p style={{ color: THEME.textMuted }} className="text-sm">No pick-up data for this period</p>
  </div>
);

export default PickupAnalytics;
